import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, User, Eye, EyeOff, Phone } from 'lucide-react';
import branding from '../config/branding';
import logo from '../logo.png';

// Same rules as the server (backend/server.js)
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 72;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// A 10-digit Indian mobile number, allowing spaces/dashes and a +91 or 0 prefix
function normalizeMobile(input) {
  let digits = input.replace(/[\s\-().]/g, '').replace(/^\+/, '');
  if (!/^\d+$/.test(digits)) return null;
  if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
  else if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
  return /^[6-9]\d{9}$/.test(digits) && !/^(\d)\1{9}$/.test(digits) ? digits : null;
}

export default function AuthPage({ mode = 'login' }) {
  const { login, register } = useAuth();
  const { t, isHindi } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const from = location.state?.from?.pathname || '/dashboard';

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setError('');
  };

  // Problem with the form as filled in, or '' when it can be sent
  const validate = () => {
    if (!EMAIL_PATTERN.test(formData.email.trim())) {
      return isHindi ? 'कृपया सही ईमेल पता दर्ज करें' : 'Please enter a valid email address';
    }
    if (mode === 'signup') {
      if (!formData.name.trim()) return isHindi ? 'कृपया अपना नाम दर्ज करें' : 'Please enter your name';
      if (!normalizeMobile(formData.phone)) {
        return isHindi ? 'कृपया सही 10 अंकों का मोबाइल नंबर दर्ज करें' : 'Please enter a valid 10-digit mobile number';
      }
      if (formData.password.length < PASSWORD_MIN_LENGTH) {
        return isHindi
          ? `पासवर्ड कम से कम ${PASSWORD_MIN_LENGTH} अक्षरों का होना चाहिए`
          : `Password must be at least ${PASSWORD_MIN_LENGTH} characters`;
      }
      if (formData.password.length > PASSWORD_MAX_LENGTH) {
        return isHindi
          ? `पासवर्ड अधिकतम ${PASSWORD_MAX_LENGTH} अक्षरों का हो सकता है`
          : `Password must be at most ${PASSWORD_MAX_LENGTH} characters`;
      }
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setLoading(true);
    setError('');

    try {
      if (mode === 'login') {
        const userData = await login(formData.email, formData.password);
        toast.success(isHindi ? 'सफलतापूर्वक लॉगिन हुआ!' : 'Successfully logged in!');
        navigate(userData.role === 'admin' ? '/admin' : from, { replace: true });
      } else {
        await register(formData.email, formData.password, formData.name, normalizeMobile(formData.phone));
        toast.success(isHindi ? 'अकाउंट बनाया गया!' : 'Account created successfully!');
        navigate(from, { replace: true });
      }
    } catch (err) {
      const message = err.response?.data?.detail || (isHindi ? 'कुछ गलत हो गया' : 'Something went wrong');
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-[#ffffff] flex" data-testid={mode === 'login' ? 'login-page' : 'signup-page'}>
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <img src={logo} alt={branding.name} className="w-12 h-12 rounded-full object-cover" />
            <div>
              <h1 className={`text-2xl font-bold text-[#121212] ${isHindi ? 'font-hindi-heading' : 'font-heading'}`}>
                {isHindi ? branding.nameHi : branding.name}
              </h1>
            </div>
          </div>

          {/* Title */}
          <h2 className={`text-3xl font-bold text-gray-900 mb-2 ${isHindi ? 'font-hindi-heading' : 'font-heading'}`}>
            {mode === 'login' 
              ? (isHindi ? 'वापस स्वागत है' : 'Welcome Back')
              : (isHindi ? 'अकाउंट बनाएं' : 'Create Account')
            }
          </h2>
          <p className={`text-gray-600 mb-8 ${isHindi ? 'font-hindi' : ''}`}>
            {mode === 'login'
              ? (isHindi ? 'अपने अकाउंट में लॉगिन करें' : 'Login to your account')
              : t('joinCommunity')
            }
          </p>

          {/* Google login is turned off for now (it also can't run inside the Android app's WebView) */}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {mode === 'signup' && (
              <div>
                <Label htmlFor="name" className={`text-gray-700 ${isHindi ? 'font-hindi' : ''}`}>
                  {t('name')}
                </Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={isHindi ? 'अपना नाम दर्ज करें' : 'Enter your name'}
                    className="pl-10 py-6 rounded-sm border-gray-300 focus:border-[#121212] focus:ring-[#121212]"
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="email" className={`text-gray-700 ${isHindi ? 'font-hindi' : ''}`}>
                {t('email')}
              </Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={isHindi ? 'अपना ईमेल दर्ज करें' : 'Enter your email'}
                  className="pl-10 py-6 rounded-sm border-gray-300 focus:border-[#121212] focus:ring-[#121212]"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            {mode === 'signup' && (
              <div>
                <Label htmlFor="phone" className={`text-gray-700 ${isHindi ? 'font-hindi' : ''}`}>
                  {isHindi ? 'मोबाइल नंबर' : 'Mobile number'}
                </Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <span className="absolute left-10 top-1/2 -translate-y-1/2 text-gray-500 select-none">+91</span>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    maxLength={14}
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="98765 43210"
                    className="pl-[4.5rem] py-6 rounded-sm border-gray-300 focus:border-[#121212] focus:ring-[#121212]"
                    required
                    data-testid="phone-input"
                  />
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="password" className={`text-gray-700 ${isHindi ? 'font-hindi' : ''}`}>
                {t('password')}
              </Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={isHindi ? 'अपना पासवर्ड दर्ज करें' : 'Enter your password'}
                  className="pl-10 pr-10 py-6 rounded-sm border-gray-300 focus:border-[#121212] focus:ring-[#121212]"
                  required
                  data-testid="password-input"
                  aria-describedby={mode === 'signup' ? 'password-hint' : undefined}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center justify-center w-10 h-10 text-gray-400 hover:text-gray-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {mode === 'signup' && (
                <p id="password-hint" className={`mt-1.5 text-xs text-gray-500 ${isHindi ? 'font-hindi' : ''}`}>
                  {isHindi ? `कम से कम ${PASSWORD_MIN_LENGTH} अक्षर` : `At least ${PASSWORD_MIN_LENGTH} characters`}
                </p>
              )}
            </div>

            {error && (
              <p className="text-red-600 text-sm" data-testid="auth-error">{error}</p>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full py-6 bg-[#121212] hover:bg-[#000000] text-white font-bold uppercase tracking-wider rounded-sm"
              data-testid="submit-btn"
            >
              {loading 
                ? (isHindi ? 'कृपया प्रतीक्षा करें...' : 'Please wait...')
                : mode === 'login' ? t('login') : t('signup')
              }
            </Button>
          </form>

        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#121212] to-[#363636]">
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        <div className="relative h-full flex items-center justify-center p-12">
          <div className="text-center text-white">
            <img src={logo} alt={branding.name} className="w-24 h-24 mx-auto mb-8 rounded-full object-cover border-4 border-white/30" />
            <h2 className={`text-4xl font-bold mb-4 ${isHindi ? 'font-hindi-heading' : 'font-heading'}`}>
              {isHindi ? branding.nameHi : branding.name}
            </h2>
            <p className={`text-xl text-white/80 max-w-md mx-auto ${isHindi ? 'font-hindi' : ''}`}>
              {isHindi 
                ? 'रिपोर्टर समुदाय में शामिल हों और अपनी खबरें साझा करें'
                : 'Join the reporter community and share your stories with the world'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
